import React from "react";
import { Users } from "react-feather";
import { BookedBucket, ConductBucket, MetricGroup } from "../../../../Api/apiUtils";
import { Badge, BadgeTone } from "../../../../ui";

/** Which measure the heatmap cells render. */
export type HmMetric = "booked" | "conduct" | "aecount";

/** conductBucket enum → cell colour class. Bands are server-owned; never re-derive them here. */
export const bucketClass = (bucket?: ConductBucket | null): string => {
  switch (bucket) {
    case "NOBOOK":
      return "hc-nobook";
    case "CRIT":
      return "hc-crit";
    case "WARN":
      return "hc-warn";
    case "GOOD":
      return "hc-good";
    case "EMPTY":
    default:
      return "hc-empty";
  }
};

/** conductBucket enum → badge tone (same bands as bucketClass). */
export const bucketTone = (bucket?: ConductBucket | null): BadgeTone => {
  switch (bucket) {
    case "CRIT":
      return "danger";
    case "WARN":
      return "warning";
    case "GOOD":
      return "success";
    default:
      return "neutral";
  }
};

/** bookedBucket enum → cell colour class (reads inversely: low fill is healthy). */
export const bookedBucketClass = (bucket?: BookedBucket | null): string => {
  switch (bucket) {
    case "GREEN":
      return "hb-green";
    case "AMBER":
      return "hb-amber";
    case "RED":
      return "hb-red";
    case "EMPTY":
    default:
      return "hb-empty";
  }
};

/** Cell colour class for the active metric mode. */
export const metricCellClass = (metric: HmMetric, m: MetricGroup): string =>
  metric === "booked" ? bookedBucketClass(m.bookedBucket) : bucketClass(m.conductBucket);

/** AE/TE-count concentration ramp — relative to the busiest window in view. */
export const aeCountCellClass = (n: number, maxN: number): string => {
  if (n === 0) return "ha-zero";
  const ratio = maxN > 0 ? n / maxN : 0;
  if (ratio <= 0.15) return "ha-1";
  if (ratio <= 0.3) return "ha-2";
  if (ratio <= 0.45) return "ha-3";
  if (ratio <= 0.6) return "ha-4";
  if (ratio <= 0.75) return "ha-5";
  if (ratio <= 0.9) return "ha-6";
  return "ha-max";
};

/** Render a percentage that may be null (booked/joined === 0). */
export const fmtPct = (pct: number | null | undefined): string =>
  pct === null || pct === undefined ? "—" : `${pct}%`;

export const roleTag = (role: "AE" | "TE"): JSX.Element =>
  role === "AE" ? <Badge tone="primary">AE</Badge> : <Badge tone="success">TE</Badge>;

/** Paid-student count badge — populated for TE rows only. */
export const studentBadge = (role: "AE" | "TE", paidStudents: number | null): JSX.Element =>
  role === "TE" && paidStudents ? (
    <Badge tone="success">
      <Users size={10} strokeWidth={2.5} aria-hidden />
      {paidStudents}
    </Badge>
  ) : (
    <span className="ds-text-subtle">—</span>
  );

/** Category chip colour bucket — first token drives the tone (north/south/other). */
export const catChipClass = (cat: string): string => {
  const c = (cat || "").toLowerCase();
  if (c.includes("north")) return "vn";
  if (c.includes("south")) return "vs";
  return "gtr";
};

const CAT_CHIP_TONES: Record<string, BadgeTone> = { vn: "success", vs: "warning", gtr: "accent" };

/** Category chip — tone from catChipClass. */
export const catChip = (cat: string): JSX.Element => <Badge tone={CAT_CHIP_TONES[catChipClass(cat)]}>{cat}</Badge>;

/** Conduct/Convert percentage cell with mini progress bar. */
export const PctCell: React.FC<{ pct: number | null; type: "conduct" | "convert" }> = ({ pct, type }) => {
  if (pct === null || pct === undefined) {
    return (
      <td className="ds-num">
        <span className="ds-text-subtle">—</span>
      </td>
    );
  }
  return (
    <td className="ds-num">
      <div className={`sa-pct sa-pct--${type}`}>
        <div className="sa-pct__track">
          {/* Width is the data value itself. */}
          <div className="sa-pct__fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="sa-pct__value">{pct}%</span>
      </div>
    </td>
  );
};

/** Extract a human message from a dashboard 400 / axios error. */
export const dashboardErrorMessage = (err: any): string => {
  const data = err?.response?.data;
  if (data?.message) return data.fix ? `${data.message} ${data.fix}` : data.message;
  if (err?.message) return err.message;
  return "Something went wrong loading the dashboard.";
};

/** Guard: from..to must be a valid ≤31-day forward range. Returns error string or null. */
export const validateRange = (from: string, to: string): string | null => {
  if (!from || !to) return "Pick both a from and to date.";
  const f = new Date(from);
  const t = new Date(to);
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return "Invalid date.";
  if (f > t) return "From date is after to date.";
  const days = Math.round((t.getTime() - f.getTime()) / 86400000) + 1;
  if (days > 31) return `Range spans ${days} days; max is 31.`;
  return null;
};
